/**
 * 剪贴板工具
 *
 * 背景: `navigator.clipboard` 只在「安全上下文」(HTTPS / localhost) 下可用。
 * 内网 cookpit 多通过 HTTP 访问, 此时 `navigator.clipboard === undefined`,
 * 直接调用 `navigator.clipboard.writeText` 会抛 TypeError, 导致复制始终失败。
 *
 * 本工具优先用异步 Clipboard API, 不可用时回退到 `document.execCommand('copy')`,
 * 兼容 HTTP 环境与老浏览器。
 */

/**
 * 用隐藏 textarea + execCommand('copy') 兜底复制 (HTTP / 老浏览器)。
 * @returns 是否复制成功
 */
function copyTextFallback(text: string): boolean {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  // 移出可视区域, 避免页面闪动与滚动跳变
  textarea.style.position = "fixed";
  textarea.style.top = "-9999px";
  textarea.style.left = "-9999px";
  textarea.setAttribute("readonly", "");
  document.body.appendChild(textarea);

  // iOS Safari 需要先聚焦再选中
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, text.length);

  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  } finally {
    document.body.removeChild(textarea);
  }
  return ok;
}

/**
 * 复制文本到剪贴板, 自动在 HTTP / 老浏览器环境下回退。
 *
 * @param text 要复制的文本
 * @returns Promise<boolean> 是否复制成功 (不抛异常, 调用方据返回值提示)
 */
export async function copyText(text: string): Promise<boolean> {
  // 优先使用异步 Clipboard API (仅安全上下文可用)
  if (
    typeof navigator !== "undefined" &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === "function" &&
    window.isSecureContext
  ) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // 权限被拒 / 非用户激活等, 继续走兜底
    }
  }
  return copyTextFallback(text);
}
